import { Link, useSearchParams } from 'react-router-dom'
import { Box, Breadcrumbs, Tab, Tabs, Typography } from '@mui/material'
import { Home as HomeIcon, NavigateNext as NavigateNextIcon } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { PageHelpPopover } from '@/components/common/PageHelpPopover'
import { SocialApprovalTab } from '@/components/admin/social/SocialApprovalTab'
import { SocialJournalTab } from '@/components/admin/social/SocialJournalTab'
import { SocialTargetsTab } from '@/components/admin/social/SocialTargetsTab'
import { ROUTES } from '@/utils/constants'

const TABS = ['approval', 'journal', 'channels'] as const
type SocialTab = (typeof TABS)[number]

/**
 * Публикации новостей в Telegram и VK: одобрение постов агента, журнал с
 * решениями по зависшим шагам и каналы с токенами. Одобрить публикацию можно
 * только кнопкой на этой странице.
 */
export function AdminSocialPage() {
  const { t } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()
  const raw = searchParams.get('tab') ?? ''
  const tab: SocialTab = (TABS as readonly string[]).includes(raw) ? (raw as SocialTab) : 'approval'

  return (
    <DashboardLayout>
      {/*
        Сетка с колонкой minmax(0, 1fr): прокручиваемые Tabs иначе задают
        минимальную ширину контента, и на мобильной ширине у страницы
        появляется горизонтальный скролл (так ведёт себя AdminFinancePage).
      */}
      <Box sx={{ p: { xs: 2, sm: 3 }, display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', alignContent: 'start' }}>
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb" sx={{ mb: 2 }}>
          <Link
            to={ROUTES.DASHBOARD}
            style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', color: 'inherit' }}
          >
            <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            {t('dashboard.home')}
          </Link>
          <Typography color="text.primary">{t('dashboard.administration')}</Typography>
          <Typography color="text.primary">{t('admin.social.title')}</Typography>
        </Breadcrumbs>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mb: 2 }}>
          <Typography variant="h4" component="h1" fontWeight={700} sx={{ flexGrow: 1 }}>
            {t('admin.social.title')}
          </Typography>
          <PageHelpPopover pageKey="admin-social" />
        </Box>

        <Tabs
          value={tab}
          onChange={(_, value: SocialTab) => setSearchParams({ tab: value })}
          sx={{ mb: 3 }}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
        >
          <Tab value="approval" label={t('admin.social.tabs.approval')} />
          <Tab value="journal" label={t('admin.social.tabs.journal')} />
          <Tab value="channels" label={t('admin.social.tabs.channels')} />
        </Tabs>

        {tab === 'approval' && <SocialApprovalTab />}
        {tab === 'journal' && <SocialJournalTab />}
        {tab === 'channels' && <SocialTargetsTab />}
      </Box>
    </DashboardLayout>
  )
}
